import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useHttpClient } from "shared/hooks/http-hook";

// API surface for the independent Bundled (multi-school) student route feature.
// Thin wrappers around useHttpClient hitting /api/bundled-route. The backend persists a dedicated
// BundledRoute document (its own collection) with an ordered stops[] chain.
const BASE = () => `${process.env.REACT_APP_BACKEND_URL}/bundled-route`;

export const useBundledRouteApi = () => {
  const { sendRequest, isLoading, error } = useHttpClient();
  const { t } = useTranslation();

  const surfaceError = useCallback(
    (err) => {
      if (err?.code === 628) return; // usage-limit already toasted inside useHttpClient
      if (err?.message) {
        window.toast?.error(err.message);
      } else if (err?.code) {
        window.toast?.error(t("httpCodes." + err.code));
      } else {
        window.toast?.error(t("actionFailed"));
      }
    },
    [t],
  );

  const listBundledRoutes = useCallback(async () => {
    try {
      const data = await sendRequest(`${BASE()}`, "GET");
      return Array.isArray(data?.bundledRoutes) ? data.bundledRoutes : [];
    } catch (err) {
      surfaceError(err);
      return [];
    }
  }, [sendRequest, surfaceError]);

  const getBundledRoute = useCallback(
    async (id) => {
      try {
        const data = await sendRequest(`${BASE()}/${id}`, "GET");
        return data?.bundledRoute || null;
      } catch (err) {
        surfaceError(err);
        return null;
      }
    },
    [sendRequest, surfaceError],
  );

  const createBundledRoute = useCallback(
    async (payload) => {
      try {
        const data = await sendRequest(`${BASE()}`, "POST", JSON.stringify(payload));
        return data?.bundledRoute || null;
      } catch (err) {
        surfaceError(err);
        return null;
      }
    },
    [sendRequest, surfaceError],
  );

  const updateBundledRoute = useCallback(
    async (id, payload) => {
      try {
        const data = await sendRequest(`${BASE()}/${id}`, "PATCH", JSON.stringify(payload));
        return data?.bundledRoute || null;
      } catch (err) {
        surfaceError(err);
        return null;
      }
    },
    [sendRequest, surfaceError],
  );

  const deleteBundledRoute = useCallback(
    async (id) => {
      try {
        await sendRequest(`${BASE()}/${id}`, "DELETE");
        return true;
      } catch (err) {
        surfaceError(err);
        return false;
      }
    },
    [sendRequest, surfaceError],
  );

  // Paired-shipment optimization. Returns { stops:[{kind,id,cumulativeSeconds}], totalDistance,
  // totalDuration, skippedStudentIds } or null on failure.
  const optimizeBundledRoute = useCallback(
    async (studentIds, isPickup) => {
      try {
        const data = await sendRequest(`${BASE()}/optimize`, "POST", JSON.stringify({ studentIds, isPickup }));
        return data?.bundledOptimization || null;
      } catch (err) {
        surfaceError(err);
        return null;
      }
    },
    [sendRequest, surfaceError],
  );

  return {
    listBundledRoutes,
    getBundledRoute,
    createBundledRoute,
    updateBundledRoute,
    deleteBundledRoute,
    optimizeBundledRoute,
    isLoading,
    error,
  };
};
